import React, { useState } from 'react';
import { CreateEventRequest, Event, FaqRequest } from '../types';

interface EventFormProps {
  event?: Event;
  onSubmit: (data: CreateEventRequest) => Promise<void>;
  onCancel: () => void;
}

const EventForm: React.FC<EventFormProps> = ({ event, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<CreateEventRequest>({
    title: event?.title || '',
    imageUrl: event?.imageUrl || '',
    bannerUrl: event?.bannerUrl || '',
    location: event?.location || '',
    instructions: event?.instructions || '',
    description: event?.description || '',
    faqs: event?.faqs?.map(f => ({ question: f.question, answer: f.answer, displayOrder: f.displayOrder })) || []
  });
  const [loading, setLoading] = useState(false);
  const [newFaq, setNewFaq] = useState<FaqRequest>({ question: '', answer: '', displayOrder: 0 });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(formData);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddFaq = () => {
    if (newFaq.question && newFaq.answer) {
      setFormData({
        ...formData,
        faqs: [...(formData.faqs || []), { ...newFaq, displayOrder: formData.faqs?.length || 0 }]
      });
      setNewFaq({ question: '', answer: '', displayOrder: 0 });
    }
  };

  const handleRemoveFaq = (index: number) => {
    setFormData({
      ...formData,
      faqs: formData.faqs?.filter((_, i) => i !== index)
    });
  };

  const handleFaqChange = (index: number, field: 'question' | 'answer', value: string) => {
    const updatedFaqs = [...(formData.faqs || [])];
    updatedFaqs[index] = { ...updatedFaqs[index], [field]: value };
    setFormData({ ...formData, faqs: updatedFaqs });
  };

  return (
    <div style={{ maxWidth: '600px', margin: '20px auto', padding: '20px' }}>
      <h2>{event ? 'Edit Event' : 'Create New Event'}</h2>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="title">Title *:</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '8px',
              marginTop: '5px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="location">Location *:</label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '8px',
              marginTop: '5px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="imageUrl">Image URL:</label>
          <input
            type="url"
            id="imageUrl"
            name="imageUrl"
            value={formData.imageUrl}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '8px',
              marginTop: '5px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="bannerUrl">Banner URL:</label>
          <input
            type="url"
            id="bannerUrl"
            name="bannerUrl"
            value={formData.bannerUrl}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '8px',
              marginTop: '5px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="instructions">Instructions:</label>
          <textarea
            id="instructions"
            name="instructions"
            value={formData.instructions}
            onChange={handleChange}
            rows={3}
            style={{
              width: '100%',
              padding: '8px',
              marginTop: '5px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="description">Description:</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            style={{
              width: '100%',
              padding: '8px',
              marginTop: '5px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>(Potensielt) stilte-spørsmål (FAQs):</label>

          {formData.faqs && formData.faqs.length > 0 && (
            <div style={{ marginTop: '10px', marginBottom: '15px' }}>
              {formData.faqs.map((faq, index) => (
                <div key={index} style={{ marginBottom: '15px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                  <div style={{ marginBottom: '10px' }}>
                    <input
                      type="text"
                      placeholder="Spørsmål"
                      value={faq.question}
                      onChange={(e) => handleFaqChange(index, 'question', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        marginBottom: '5px'
                      }}
                    />
                    <textarea
                      placeholder="Svar"
                      value={faq.answer}
                      onChange={(e) => handleFaqChange(index, 'answer', e.target.value)}
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFaq(index)}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Fjern
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ padding: '10px', border: '1px solid #28a745', borderRadius: '4px', backgroundColor: '#f8fff9' }}>
            <input
              type="text"
              placeholder="Nytt spørsmål"
              value={newFaq.question}
              onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '5px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            />
            <textarea
              placeholder="Svar"
              value={newFaq.answer}
              onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
              rows={2}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '5px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            />
            <button
              type="button"
              onClick={handleAddFaq}
              disabled={!newFaq.question || !newFaq.answer}
              style={{
                padding: '8px 15px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: (!newFaq.question || !newFaq.answer) ? 'not-allowed' : 'pointer',
                opacity: (!newFaq.question || !newFaq.answer) ? 0.6 : 1
              }}
            >
              Legg til spørsmål
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Saving...' : (event ? 'Update Event' : 'Create Event')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventForm;